import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Theme,
  Tooltip,
  useMediaQuery,
} from '@mui/material';
import { saveAs } from 'file-saver';
import { useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import FileCard from 'src/components/atoms/file-card/file-card';

import { BulkAction, BulkActionsMenu } from '../../../../components/atoms/bulk-actions-menu';
import { Scrollbar } from '../../../../components/atoms/scrollbar';
import UploadDialog from '../../../../components/organisms/upload-dialog/upload-dialog';
import { useDialog } from '../../../../hooks/use-dialog';
import { useSelection } from '../../../../hooks/use-selection';
import { useAddAttachment } from '../../hooks/mutation/use-add-attachment';
import { DeleteAttachmentDialog } from '../organisms/dialog/delete-attachment-dialog';

import { DataStatusIndicator } from '@/components/molecules/data-status-indicator/data-status-indicator';
import { useAttachments } from '@/features/visits/hooks/query/use-attachments';
import { Can, usePermissions } from '@/permissions/can';

interface Props {
  visitId: number;
}

const MAX_FILE_SIZE_IN_BYTES = 15728640;

export const VisitAttachments = ({ visitId }: Props) => {
  const { t } = useTranslation(['visit', 'common']);

  const permissions = usePermissions();

  const lgDown = useMediaQuery((theme: Theme) => theme.breakpoints.down('lg'));
  const xlDown = useMediaQuery((theme: Theme) => theme.breakpoints.down('xl'));

  const [isAddAttachmentDialogSubmitting, setIsAddAttachmentDialogSubmitting] = useState(false);

  const columns = [
    {
      id: 'name',
      column: t('visit:attachments.name'),
    },
  ];

  const [selectedAttachments, handleSelect, handleSelectAll, handleClearSelectedAttachments] =
    useSelection<number>();

  const [isOpenAddAttachmentDialog, handleOpenAddAttachmentDialog, handleCloseAddAttachmentDialog] =
    useDialog();

  const { mutateAsync: addAttachment } = useAddAttachment();

  const { data: attachments, isLoading, error } = useAttachments(visitId);

  const [
    isOpenDeleteAttachmentDialog,
    handleOpenDeleteAttachmentDialog,
    handleCloseDeleteAttachmentDialog,
  ] = useDialog();

  const actions: BulkAction[] = [
    {
      id: 'download',
      label: t('visit:attachments.downloadSelected'),
      onClick: () => {
        selectedAttachments.forEach(attachmentId => {
          const attachment = attachments?.find(attachment => attachment.id === attachmentId);
          saveAs(attachment?.url || '', attachment?.fileName);
        });
        handleClearSelectedAttachments();
      },
      disabled: selectedAttachments.length === 0,
    },
    {
      id: 'delete',
      label: t('visit:attachments.deleteSelected'),
      onClick: handleOpenDeleteAttachmentDialog,
      disabled: selectedAttachments.length === 0 || permissions.cannot('update', 'Visitors'),
      tooltip: permissions.cannot('update', 'Visitors')
        ? t('common:notSufficientPermissions')
        : undefined,
    },
  ];

  const onCancelDeleteAttachmentDialog = () => {
    handleCloseDeleteAttachmentDialog();
    handleClearSelectedAttachments();
  };

  return (
    <>
      <Card variant="outlined">
        <CardHeader
          title={t('visit:attachments.title')}
          sx={{ p: 2 }}
          action={
            <Box
              sx={{
                display: 'flex',
                flexDirection: xlDown && !lgDown ? 'column' : 'row',
                alignItems: xlDown && !lgDown ? 'flex-end' : 'center',
                gap: 1,
                ml: 1,
                flexWrap: 'wrap',
              }}>
              <Can I="update" a="Visitors" passThrough>
                {allowed => (
                  <Tooltip
                    title={t('common:notSufficientPermissions')}
                    disableHoverListener={allowed}
                    disableFocusListener={allowed}>
                    <span>
                      <BulkActionsMenu
                        title={t('common:bulkActions')}
                        size="medium"
                        actions={actions}
                        disabled={selectedAttachments.length === 0 || !allowed}
                        selectedCount={selectedAttachments.length}
                      />
                    </span>
                  </Tooltip>
                )}
              </Can>

              <Can I="update" a="Visitors" passThrough>
                {allowed => (
                  <Tooltip
                    title={t('common:notSufficientPermissions')}
                    disableHoverListener={allowed}
                    disableFocusListener={allowed}>
                    <span>
                      <Button
                        variant="contained"
                        onClick={handleOpenAddAttachmentDialog}
                        disabled={!allowed}>
                        {t('common:button.add')}
                      </Button>
                    </span>
                  </Tooltip>
                )}
              </Can>
            </Box>
          }
        />
        <Divider />
        <CardContent
          style={{
            padding: 0,
          }}>
          <Scrollbar>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={
                        attachments &&
                        attachments?.length > 0 &&
                        selectedAttachments.length === attachments?.length
                      }
                      indeterminate={
                        selectedAttachments.length > 0 &&
                        selectedAttachments.length < (attachments ? attachments : [])?.length
                      }
                      onChange={e =>
                        handleSelectAll(
                          e.target.checked,
                          attachments?.map(attachment => attachment.id) || [],
                        )
                      }
                    />
                  </TableCell>
                  {columns.map(column => (
                    <TableCell key={column.id}>{column.column}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {attachments?.map(file => (
                  <TableRow
                    key={file.id}
                    hover
                    selected={
                      !!selectedAttachments.find(
                        selectedAttachment => selectedAttachment === file.id,
                      )
                    }>
                    <TableCell sx={{ padding: 0.5 }}>
                      <Checkbox
                        checked={
                          !!selectedAttachments.find(
                            selectedAttachment => selectedAttachment === file.id,
                          )
                        }
                        onChange={event => handleSelect(event.target.checked, file.id)}
                      />
                    </TableCell>
                    <TableCell>
                      <FileCard
                        size={file.fileSize}
                        path={file.fileName}
                        onDownload={() => {
                          saveAs(file.url, file.fileName);
                        }}
                        onDelete={() => {
                          handleOpenDeleteAttachmentDialog();
                          handleClearSelectedAttachments();
                          handleSelect(true, file.id);
                        }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Scrollbar>
          <DataStatusIndicator
            dataLength={attachments?.length ?? 0}
            error={error?.response?.data?.message}
            isFetching={isLoading}
          />
        </CardContent>
      </Card>
      <UploadDialog
        title={t('visit:attachments.dialog.add.title')}
        fileDropzoneDescription={t('visit:attachments.dialog.add.eachFileShouldNotExceed')}
        fileDropzoneTitle={t('visit:attachments.dialog.add.browseOrDropFile')}
        open={isOpenAddAttachmentDialog}
        onCancel={handleCloseAddAttachmentDialog}
        isSubmitting={isAddAttachmentDialogSubmitting}
        maxFileSize={MAX_FILE_SIZE_IN_BYTES}
        filesAcceptTypes={{
          'image/jpeg': ['.jpeg'],
          'image/png': ['.png'],
          'application/pdf': ['.pdf'],
        }}
        onSubmit={async files => {
          try {
            setIsAddAttachmentDialogSubmitting(true);
            const addAttachmentMutations = files.map(file =>
              addAttachment({ visitId: visitId.toString(), file }),
            );
            await Promise.allSettled(addAttachmentMutations).then(results => {
              const statuses = results.map(result => result.status);
              const hasRejected = statuses.includes('rejected');
              const hasFulfilled = statuses.includes('fulfilled');

              if (hasRejected && !hasFulfilled) {
                toast.error(t('visit:attachments.dialog.add.failed'));
              }
              if (hasRejected && hasFulfilled) {
                toast.error(t('visit:attachments.dialog.add.warning'));
              }
              if (!hasRejected && hasFulfilled) {
                toast.success(t('visit:attachments.dialog.add.success'));
              }
            });
            handleCloseAddAttachmentDialog();
          } finally {
            setIsAddAttachmentDialogSubmitting(false);
          }
        }}
      />
      <DeleteAttachmentDialog
        open={isOpenDeleteAttachmentDialog}
        onCancel={onCancelDeleteAttachmentDialog}
        selectedAttachments={selectedAttachments}
        visitId={visitId}
      />
    </>
  );
};
